import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export type PolicyScope = 'self' | 'subcommittee' | 'district' | 'council';
export interface PolicyGrant { capabilityKey: string; scopeCeiling: PolicyScope; state: 'granted' }
export interface PolicyEntry { positionId: string; grants: PolicyGrant[] }
export type RelationshipRole = 'primary_coordinator' | 'manager' | 'unit_leader';
export interface RoleGrant { capabilityKey: string; state: 'granted' }
export interface RoleGrantEntry { role: RelationshipRole; grants: RoleGrant[] }
export interface PolicyRevision {
  revisionId: string; revisionNumber: number; state: 'draft' | 'published';
  owner: { kind: 'council' | 'compassDefault'; ownerId: string };
  createdAt: string; createdByUid: string; previousRevisionId: string | null;
  reason: string | null; policy: { entries: PolicyEntry[]; relationshipRoleGrants?: RoleGrantEntry[] }; fingerprint: string | null;
  publishedAt: string | null; publishedByUid: string | null;
}
export interface CapabilityOption {
  key: string; domain: string; label: string; description: string;
  scopes: PolicyScope[]; sensitivity: 'standard' | 'sensitive' | 'highImpact'; requires: string[];
  enforcement: {
    status: 'policyEnforced' | 'separateAccessRules' | 'notYetEnforcedHere';
    note: string;
  };
}
export interface RelationshipRoleOption {
  role: RelationshipRole; label: string; description: string;
  capabilities: Array<Pick<CapabilityOption, 'key' | 'label' | 'description' | 'sensitivity' | 'requires' | 'enforcement'>>;
}
export interface RoleGrantDiffItem { role: RelationshipRole; capabilityKey: string; kind: 'added' | 'removed' }
export interface PositionOption {
  positionId: string; displayName: string; organizationLevel: 'district' | 'council';
  classIds: string[]; configured: boolean; configuredDisplayName: string | null;
  assignmentCount: number; source: 'council'; protected: boolean;
}
export interface PolicyDiffItem {
  positionId: string; capabilityKey: string; kind: 'added' | 'removed' | 'scopeChanged';
  previousScope: PolicyScope | null; nextScope: PolicyScope | null;
}
export interface ValidationError {
  code: string; path: string; message: string; positionId: string | null; capabilityKey: string | null; role?: string | null;
}
export interface PermissionsAdministrationData {
  policySet: { activeRevisionId: string; activeRevisionNumber: number; activeFingerprint: string; draftRevisionId: string | null; draftEditVersion: number | null };
  active: PolicyRevision; draft: PolicyRevision | null;
  draftMetadata: { editVersion: number; baseRevisionId: string | null; baseFingerprint: string | null; updatedAt: unknown; updatedByUid: string } | null;
  revisions: PolicyRevision[]; capabilities: CapabilityOption[]; relationshipRoles: RelationshipRoleOption[]; positions: PositionOption[];
  protectedBaselines: unknown[]; audit: Array<Record<string, unknown>>; authorityNotes: string[];
}

export function usePermissionsAdministration() {
  const [data, setData] = useState<PermissionsAdministrationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const call = httpsCallable<Record<string, never>, PermissionsAdministrationData>(functions, 'getPermissionsAdministrationV2');
      setData((await call({})).data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to load Permissions policy.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const createDraft = useCallback(async (reason: string) => {
    const call = httpsCallable<{ reason: string | null }, unknown>(functions, 'createPermissionsPolicyDraftV2');
    await call({ reason: reason.trim() || null }); await refresh();
  }, [refresh]);

  const updateDraft = useCallback(async (draftRevisionId: string, expectedEditVersion: number, reason: string, policy: PolicyRevision['policy']) => {
    const call = httpsCallable<unknown, { valid: boolean; errors: ValidationError[] }>(functions, 'updatePermissionsPolicyDraftV2');
    const result = await call({ draftRevisionId, expectedEditVersion, reason: reason.trim() || null, policy });
    if (result.data.valid) await refresh();
    return result.data;
  }, [refresh]);

  const validateDraft = useCallback(async (draftRevisionId: string, expectedEditVersion: number) => {
    const call = httpsCallable<unknown, { valid: boolean; errors: ValidationError[]; diff: PolicyDiffItem[]; roleDiff: RoleGrantDiffItem[]; canonicalFingerprint: string | null }>(functions, 'validatePermissionsPolicyDraftV2');
    return (await call({ draftRevisionId, expectedEditVersion })).data;
  }, []);

  const publishDraft = useCallback(async (draftRevisionId: string, expectedEditVersion: number, expectedActiveRevisionId: string) => {
    const call = httpsCallable<unknown, unknown>(functions, 'publishPermissionsPolicyDraftV2');
    await call({ draftRevisionId, expectedEditVersion, expectedActiveRevisionId }); await refresh();
  }, [refresh]);

  return { data, loading, error, refresh, createDraft, updateDraft, validateDraft, publishDraft };
}
